// Types to support questionnaires (like check-in flow) all around the codebase.

/** Question kinds - point how the question should be answered (i.e. how
 *  AnswerT objects should be interpreted for this question in its options,
 *  and answers, and what UI should be used to present the question, and
 *  to collect the answers, etc. */
export enum QuestionKinds {
  ANSWER_OPTIONS = 'ANSWER_OPTIONS',
  FREE_FORM_ANSWER = 'FREE_FORM_ANSWER',
  MOOD_OPTIONS = 'MOOD_OPTIONS',
  SLIDER_ANSWER = 'SLIDER_ANSWER',

  /** Technically, this is the same question kind as the generic SLIDER_ANSWER,
   *  but it serves to uniquely identify this very question about the target
   *  session duration in all questionnaries. */
  TARGET_SESSION_DURATION = 'TARGET_SESSION_DURATION',
}

export enum QuestionnaireKinds {

  /** Onboarding AI interview. */
  ONBOARDING_AI_INTERVIEW = 'ONBOARDING_AI_INTERVIEW',

  POST_SESSION = 'POST_SESSION',

  /** Post-session interview driven by AI Realtime Agent. */
  POST_SESSION__AI_REALTIME = 'POST_SESSION__AI_REALTIME',

  WELLNESS_CHECKIN = 'WELLNESS_CHECKIN',

  /** Wellness check-in interview driven by AI Realtime Agent. */
  WELLNESS_CHECKIN__AI_REALTIME = 'WELLNESS_CHECKIN__AI_REALTIME',
}

/** An abstract question answer - a tuple with numeric and string elements. */
export type AnswerT = [number, string];

export function isAnswer(o: unknown): o is AnswerT {
  return Array.isArray(o) && o.length === 2
    && typeof o[0] === 'number' && typeof o[1] === 'string';
}

export function isAnswersArray(o: unknown): o is AnswerT [] {
  if (!Array.isArray(o)) return false;
  for (const it of o) {
    if (!isAnswer(it)) return false;
  }
  return true;
}

/**
 * T should be Blob for client-side; Buffer for server-side.
 */
export type AnswersOrAudioT<T extends Blob | Buffer> = AnswerT[] | {
  audio: T;
  duration: number; // [ms]
};

export type QuestionT = {
  kind: QuestionKinds;
  options: AnswerT[];
  question: string;
};

export type QuestionWithAnswersT = QuestionT & {
  /** It is an array to allow for questions with multiple answers permitted. */
  answers: AnswerT[];
};

function areEqualAnswers(a: AnswerT, b: AnswerT): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

export function areValidAnswers(
  answers: AnswerT[],
  question: QuestionT,
): boolean {
  switch (question.kind) {
    case QuestionKinds.ANSWER_OPTIONS:
    case QuestionKinds.MOOD_OPTIONS:
      return answers.length === 1
        && question.options.some((o) => {
          const [a] = answers;
          if (!a) throw Error('Internal error');
          return areEqualAnswers(a, o);
        });
    case QuestionKinds.FREE_FORM_ANSWER:
      return answers.length === 1;

    // TODO: Actually, for the session duration question we may apply stricter
    // validation, checking the format of text label!
    case QuestionKinds.SLIDER_ANSWER:
    case QuestionKinds.TARGET_SESSION_DURATION: {
      if (answers.length !== 1) return false;

      const [min] = question.options;
      if (!min) throw Error('Internal error');
      const max = question.options[question.options.length - 1];
      if (!max) throw Error('Internal error');

      const answer = answers[0]?.[0];
      if (answer === undefined) throw Error('Missing answer');

      return answer >= min[0] && answer <= max[0];
    }
    default: throw Error('Invalid question kind');
  }
}

export type QuestionnaireT = {
  /** Completion timestamp, if completed. */
  completedAt?: null | number;

  /** Questionnaire ID. "undefined" before questionnaire is stored to DB. */
  id?: string;

  /**
   * Questions and answers. Can be null, as Questionnaire objects can be
   * created with just a raw transcript, with items added later, once the processing
   * of raw transcript is completed.
   */
  items: QuestionWithAnswersT[] | null;

  /** Questionnaire kind. */
  kind: QuestionnaireKinds;

  /**
   * The total number of questions expected at the moment (may change in
   * course of the questionnaire, based on the previous questions and answers).
   */
  numQuestionsExpected: number;

  /** ID of the related experience session, if any (if the questionnaire was
   *  taken to evaluate a completed training session). */
  sessionId?: null | string;

  /** Timestamp of the questionnaire start. */
  startedAt: number;

  /** ID of the corresponding raw transcript record, if exists. */
  transcriptId: null | string;

  /** ID of the user filling in this questionnaire. */
  userId: string;
};
